import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { formOptions } from '../../constants/formOptions';
import { MAX_COLOCADORES_POR_LICITACION } from '../../constants/colocadores';
import { addBusinessDays, toIsoDate } from '../../utils/dateHelpers';
import {
  formatCuitForDisplay,
  formatNumberForDisplay,
  parseCuitDigits,
  parseNumberForState,
} from '../../utils/formatters';
import { requiereTipoCambio } from '../../utils/licitacionesCalculator';
import {
  colocadoresDeLicitacion,
  feeToForm,
  incluyeAxon,
  mercadoDeLicitacion,
  nombresColocadores,
  parseFeeBps,
} from '../../utils/colocadores';
import { describeCuitError } from '../../utils/cuit';
import { createLicitacion, updateLicitacionConFee } from '../../services/licitacionesService';
import { useLicitacionFee } from '../../hooks/useLicitacionFee';
import { useColocadores } from '../../hooks/useColocadores';
import { TrashIcon, PlusIcon } from '../Icons';

const emptyEspecie = (id) => ({
  id,
  denominacion: '',
  clase: '',
  // Nro de Colocación del instrumento en el sistema externo: es la columna `id`
  // del Excel de carga que arman los traders.
  nroColocacion: '',
  modalidad: 'Tasa',
  monedaEmision: 'Pesos',
  monedasIntegracion: ['Pesos'],
  tasaCorte: 0,
  precioCorte: 0,
  // Solo aplica a especies en USD que se integran en pesos (dólar linked /
  // duales). Se deja vacío: recién se conoce al cierre del día T-1.
  tipoCambio: '',
  estado: 'Pendiente',
});

// El TC se edita como texto en formato es-AR ("1.474,50") y se persiste numérico.
const especieToForm = (especie) => ({
  ...especie,
  tipoCambio: especie.tipoCambio ? formatNumberForDisplay(especie.tipoCambio) : '',
});

const initialFormState = {
  nombre: '',
  emisor: '',
  // Texto libre opcional (antes "Colocador / Mercado"). En las licitaciones anteriores a
  // los colocadores se guarda en `colocador`; en las nuevas, en `mercado`.
  mercado: '',
  // Constante en toda la subasta: es la columna `agente` del Excel de carga.
  nroAgente: '',
  fechaLicitacion: '',
  fechaLiquidacion: '',
  fechaCaja: '',
  observaciones: '',
  // Solo si Axon Capital es colocador: el fee lo paga la emisora.
  cuitEmisora: '',
};

const esEspecieUsd = (especie) => especie?.monedaEmision === 'USD';

const PCT_FORMAT = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const ERROR_COLOR = '#B83227';
const ayudaStyle = { fontSize: '0.72rem', color: '#465163', marginTop: '0.25rem', lineHeight: 1.35 };
const errorStyle = { ...ayudaStyle, color: ERROR_COLOR };
const inputConError = (conError) => (conError ? { ...fieldStyles.input, borderColor: ERROR_COLOR } : fieldStyles.input);

/** Fee de un colocador: obligatorio (0 = sin fee, confirmado). '' si está bien. */
const describeErrorFeeColocador = (texto) => {
  const fee = parseFeeBps(texto);
  if (fee === null) return 'Ingresá el fee en bps (0 si este colocador no paga fee).';
  if (Number.isNaN(fee)) return 'Ingresá un número de bps entre 0 y 10.000 (ej: 25).';
  return '';
};

const describeFeeAyuda = (feeBps) => {
  if (feeBps > 0) return `${PCT_FORMAT.format(feeBps / 100)} % del monto adjudicado de cada orden.`;
  if (feeBps === 0) return 'Sin fee: queda confirmado que no cobra.';
  return 'Fee esperado: fee × monto adjudicado. El ingreso es lo que se cobra.';
};

const mismosFees = (actuales, nuevos) => {
  const a = actuales || {};
  const claves = Object.keys(nuevos);
  return claves.length === Object.keys(a).length && claves.every(id => a[id] === nuevos[id]);
};

const chipStyle = (seleccionado) => ({
  display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.45rem 0.7rem',
  border: `1px solid ${seleccionado ? '#1A283E' : '#E5E7EB'}`, borderRadius: '6px',
  backgroundColor: seleccionado ? '#EBEDED' : '#FFFFFF', fontSize: '0.85rem', cursor: 'pointer',
});

// Ids de especie correlativos que nunca se reciclan (esp-1, esp-2, ...).
const nextEspecieId = (especies) => {
  const maxN = especies.reduce((max, e) => {
    const n = parseInt(String(e.id).replace('esp-', ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `esp-${maxN + 1}`;
};

/**
 * Modal de alta/edición de una licitación (evento + especies embebidas).
 * Controlado por el padre. La fechaCaja se autocompleta con el día hábil
 * anterior a la liquidación, pero queda editable (feriados no contemplados
 * por addBusinessDays).
 */
const LicitacionFormModal = ({ isOpen, onClose, onSaved, editingLicitacion, ordenes, userName, userId }) => {
  const [form, setForm] = useState(initialFormState);
  const [especies, setEspecies] = useState([emptyEspecie('esp-1')]);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Colocadores elegidos ({ id, nombre, cuit }, copia del catálogo) y el fee tipeado de cada uno.
  const [seleccion, setSeleccion] = useState([]);
  const [feeInputs, setFeeInputs] = useState({});
  // Fee único de las licitaciones anteriores a los colocadores (si no se les agregan).
  const [feeLegacyInput, setFeeLegacyInput] = useState('');
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const didInit = useRef(false);
  const didInitFee = useRef(false);

  const catalogo = useColocadores({ enabled: isOpen });

  // El fee vive en `licitacionesFees` (solo personal interno). Mientras se lee, los
  // colocadores y sus fees quedan deshabilitados y guardar no los toca.
  const feeActual = useLicitacionFee(editingLicitacion?.id, { enabled: isOpen && !!editingLicitacion });
  const feeDisponible = !editingLicitacion || (!feeActual.isLoading && !feeActual.error);

  const colocadoresOriginales = useMemo(() => colocadoresDeLicitacion(editingLicitacion), [editingLicitacion]);
  // Obligatorio en el alta y en las que ya tienen colocadores; las anteriores pueden
  // seguir sin ellos (o agregarlos).
  const requiereColocadores = !editingLicitacion || colocadoresOriginales.length > 0;
  const usaColocadores = requiereColocadores || seleccion.length > 0;
  const axonSeleccionado = incluyeAxon(seleccion);

  // Activos del catálogo más los que la licitación ya tenía aunque se hayan desactivado.
  const opcionesColocador = useMemo(() => {
    const activos = catalogo.activos.map(({ id, nombre, cuit }) => ({ id, nombre, cuit, inactivo: false }));
    const ids = new Set(activos.map(c => c.id));
    const anteriores = colocadoresOriginales.filter(c => !ids.has(c.id)).map(c => ({ ...c, inactivo: true }));
    return [...activos, ...anteriores];
  }, [catalogo.activos, colocadoresOriginales]);

  const errorColocadores = requiereColocadores && seleccion.length === 0 ? 'Elegí al menos un colocador.' : '';
  const erroresFee = Object.fromEntries(seleccion.map(c => [c.id, describeErrorFeeColocador(feeInputs[c.id])]));
  const errorCuitEmisora = axonSeleccionado ? describeCuitError(form.cuitEmisora) : '';
  const feeLegacy = parseFeeBps(feeLegacyInput);
  const errorFeeLegacy = !usaColocadores && Number.isNaN(feeLegacy) ? 'Ingresá un número de bps entre 0 y 10.000.' : '';

  useEffect(() => {
    if (!isOpen) { didInitFee.current = false; return; }
    if (didInitFee.current) return;
    if (!editingLicitacion) {
      didInitFee.current = true;
      setFeeInputs({});
      setFeeLegacyInput('');
      return;
    }
    if (feeActual.isLoading) return;
    didInitFee.current = true;
    const fees = feeActual.feesPorColocador || {};
    setFeeInputs(Object.fromEntries(Object.entries(fees).map(([id, bps]) => [id, feeToForm(bps)])));
    setFeeLegacyInput(feeToForm(feeActual.feeBps));
  }, [isOpen, editingLicitacion, feeActual.isLoading, feeActual.feesPorColocador, feeActual.feeBps]);

  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');
    setIsSaving(false);
    setIntentoGuardar(false);

    if (editingLicitacion) {
      setForm({
        nombre: editingLicitacion.nombre || '',
        emisor: editingLicitacion.emisor || '',
        mercado: mercadoDeLicitacion(editingLicitacion),
        nroAgente: editingLicitacion.nroAgente || '',
        fechaLicitacion: editingLicitacion.fechaLicitacion || '',
        fechaLiquidacion: editingLicitacion.fechaLiquidacion || '',
        fechaCaja: editingLicitacion.fechaCaja || '',
        observaciones: editingLicitacion.observaciones || '',
        cuitEmisora: formatCuitForDisplay(editingLicitacion.cuitEmisora || ''),
      });
      setEspecies((editingLicitacion.especies || []).map(especieToForm));
      setSeleccion(colocadoresDeLicitacion(editingLicitacion).map(({ id, nombre, cuit }) => ({ id, nombre, cuit })));
      return;
    }
    setForm(initialFormState);
    setEspecies([emptyEspecie('esp-1')]);
    setSeleccion([]);
  }, [isOpen, editingLicitacion]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleToggleColocador = (colocador) => {
    const yaElegido = seleccion.some(c => c.id === colocador.id);
    if (!yaElegido && seleccion.length >= MAX_COLOCADORES_POR_LICITACION) {
      setFormError(`Una licitación puede tener hasta ${MAX_COLOCADORES_POR_LICITACION} colocadores.`);
      return;
    }
    setSeleccion(prev => (yaElegido
      ? prev.filter(c => c.id !== colocador.id)
      : [...prev, { id: colocador.id, nombre: colocador.nombre, cuit: colocador.cuit }]));
  };

  const handleFeeColocadorChange = (id, value) => {
    setFeeInputs(prev => ({ ...prev, [id]: value }));
  };

  const handleFechaLiquidacionChange = (e) => {
    const fechaLiquidacion = e.target.value;
    setForm(prev => ({
      ...prev,
      fechaLiquidacion,
      fechaCaja: fechaLiquidacion
        ? toIsoDate(addBusinessDays(new Date(`${fechaLiquidacion}T00:00`), -1))
        : prev.fechaCaja,
    }));
  };

  const handleEspecieChange = (index, field, value) => {
    setEspecies(prev => prev.map((esp, i) => (i === index ? { ...esp, [field]: value } : esp)));
  };

  const handleMonedaIntegracionToggle = (index, moneda) => {
    setEspecies(prev => prev.map((esp, i) => {
      if (i !== index) return esp;
      const set = new Set(esp.monedasIntegracion);
      if (set.has(moneda)) set.delete(moneda);
      else set.add(moneda);
      return { ...esp, monedasIntegracion: [...set] };
    }));
  };

  // Todas las especies en USD de una misma licitación usan el mismo TC (Com. A 3500
  // T-1): las dólar linked / duales para integrar y todas para pasar a pesos el fee de
  // colocación, que se cobra en pesos. Se copia de una vez y no se tipea tres veces con
  // riesgo de que una quede distinta.
  const especiesConTipoCambio = especies.filter(esEspecieUsd).length;

  const handleAplicarTipoCambioATodas = (tipoCambio) => {
    setEspecies(prev => prev.map(esp => (esEspecieUsd(esp) ? { ...esp, tipoCambio } : esp)));
  };

  const especieTieneOrdenes = (especieId) =>
    (ordenes || []).some(o => o.especieId === especieId && o.estado !== 'Eliminada');

  const handleAddEspecie = () => {
    setEspecies(prev => [...prev, emptyEspecie(nextEspecieId(prev))]);
  };

  const handleRemoveEspecie = (index) => {
    const especie = especies[index];
    if (especieTieneOrdenes(especie.id)) {
      setFormError('No se puede quitar una especie que ya tiene órdenes cargadas.');
      return;
    }
    setFormError('');
    setEspecies(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Desde el primer intento se marcan en rojo los colocadores y fees que falten.
    setIntentoGuardar(true);
    if (!form.nombre.trim()) { setFormError('Ingresá el nombre de la licitación.'); return; }
    if (!form.fechaLicitacion || !form.fechaLiquidacion || !form.fechaCaja) {
      setFormError('Completá las fechas de licitación, liquidación y caja.');
      return;
    }
    if (form.fechaLiquidacion < form.fechaLicitacion) {
      setFormError('La fecha de liquidación no puede ser anterior a la de licitación.');
      return;
    }
    if (especies.length === 0) { setFormError('Cargá al menos una especie.'); return; }
    if (especies.some(esp => !esp.denominacion.trim())) {
      setFormError('Todas las especies deben tener denominación.');
      return;
    }
    if (especies.some(esp => esp.monedasIntegracion.length === 0)) {
      setFormError('Cada especie debe aceptar al menos una moneda de integración.');
      return;
    }
    if (feeDisponible) {
      if (!editingLicitacion && catalogo.error) {
        setFormError('No se pudo leer el catálogo de colocadores. Revisá tu conexión e intentá de nuevo.');
        return;
      }
      if (errorColocadores || Object.values(erroresFee).some(Boolean) || errorCuitEmisora) {
        setFormError('Revisá los colocadores: cada uno necesita su fee y, si Axon Capital es colocador, el CUIT de la emisora.');
        return;
      }
      if (errorFeeLegacy) {
        setFormError('El fee de colocación tiene que ser un número de bps entre 0 y 10.000 (ej: 25).');
        return;
      }
    }
    setFormError('');
    setIsSaving(true);

    const payload = {
      nombre: form.nombre.trim(),
      emisor: form.emisor.trim(),
      nroAgente: form.nroAgente.trim(),
      fechaLicitacion: form.fechaLicitacion,
      fechaLiquidacion: form.fechaLiquidacion,
      fechaCaja: form.fechaCaja,
      especies: especies.map(esp => ({
        ...esp,
        denominacion: esp.denominacion.trim(),
        clase: esp.clase.trim(),
        tasaCorte: parseFloat(esp.tasaCorte) || 0,
        precioCorte: parseFloat(esp.precioCorte) || 0,
        tipoCambio: parseFloat(parseNumberForState(String(esp.tipoCambio ?? ''))) || 0,
      })),
      observaciones: form.observaciones,
    };

    const mercado = form.mercado.trim();
    const colocadores = seleccion.map(({ id, nombre, cuit }) => ({ id, nombre, cuit }));
    const feesPorColocador = Object.fromEntries(colocadores.map(c => [c.id, parseFeeBps(feeInputs[c.id])]));
    const datosColocadores = {
      colocadores,
      // Texto de siempre, para lo que todavía lee `colocador` (p. ej. el conector AUNE).
      colocador: nombresColocadores(colocadores),
      mercado,
    };
    const cuitEmisora = parseCuitDigits(form.cuitEmisora);

    try {
      const actor = { userId, userName };
      if (!editingLicitacion) {
        await createLicitacion(
          { ...payload, ...datosColocadores, ...(axonSeleccionado ? { cuitEmisora } : {}) },
          actor,
          feesPorColocador,
        );
      } else if (!feeDisponible) {
        // No se pudo leer el fee: colocadores y fees quedan como estaban.
        const extra = colocadoresOriginales.length > 0 ? { mercado } : { colocador: mercado };
        await updateLicitacionConFee(editingLicitacion.id, { ...payload, ...extra }, undefined, actor);
      } else if (usaColocadores) {
        let cuitUpdate = {};
        if (axonSeleccionado) cuitUpdate = { cuitEmisora };
        else if (editingLicitacion.cuitEmisora) cuitUpdate = { cuitEmisora: '' };
        // undefined = no tocar el fee. Al reemplazar el documento se conserva el fee único
        // que tenía una licitación anterior.
        const feeUpdate = mismosFees(feeActual.feesPorColocador, feesPorColocador) ? undefined : {
          feesPorColocador,
          ...(Number.isFinite(feeActual.feeBps) ? { feeColocacionBps: feeActual.feeBps } : {}),
        };
        await updateLicitacionConFee(
          editingLicitacion.id, { ...payload, ...datosColocadores, ...cuitUpdate }, feeUpdate, actor,
        );
      } else {
        // Licitación anterior a los colocadores que sigue sin ellos: como siempre.
        const feeCambio = feeLegacy !== (feeActual.feeBps ?? null);
        await updateLicitacionConFee(
          editingLicitacion.id,
          { ...payload, colocador: mercado },
          feeCambio ? { feeColocacionBps: feeLegacy } : undefined,
          actor,
        );
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error guardando la licitación:', error);
      setFormError('No se pudo guardar la licitación. Intentá de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const mostrarError = (error, texto) => !!error && (intentoGuardar || !!String(texto ?? '').trim());

  const renderFeeLegacy = () => (
    <div style={{ maxWidth: '320px', marginTop: '0.75rem' }}>
      <label style={fieldStyles.label} htmlFor="fee-legacy">Fee de colocación (bps, opcional)</label>
      <input
        id="fee-legacy" type="text" inputMode="decimal" value={feeLegacyInput}
        onChange={(e) => setFeeLegacyInput(e.target.value)}
        style={inputConError(!!errorFeeLegacy)} placeholder="Ej: 25"
        aria-invalid={!!errorFeeLegacy} aria-describedby="fee-legacy-ayuda"
      />
      <div id="fee-legacy-ayuda" style={errorFeeLegacy ? errorStyle : ayudaStyle}>
        {errorFeeLegacy || describeFeeAyuda(feeLegacy)}
      </div>
    </div>
  );

  const renderColocadores = () => {
    const header = (
      <div style={{ ...fieldStyles.sectionHeader, marginTop: '1.25rem' }}>Colocadores y fee de colocación</div>
    );
    if (!feeDisponible) {
      return (<>
        {header}
        <p style={ayudaStyle}>
          {feeActual.isLoading
            ? 'Cargando los fees...'
            : 'No se pudo leer el fee actual: los colocadores y sus fees se conservan sin cambios.'}
        </p>
      </>);
    }
    const cuitConError = mostrarError(errorCuitEmisora, form.cuitEmisora);
    return (<>
      {header}
      <p style={{ ...ayudaStyle, fontSize: '0.78rem', margin: '0.6rem 0' }}>
        {requiereColocadores
          ? 'Elegí por quién se colocan las órdenes y el fee que paga cada uno sobre el monto adjudicado de sus órdenes (0 = no paga fee).'
          : 'Licitación cargada antes de los colocadores: podés agregarlos. Si no, sigue con su fee de siempre.'}
      </p>
      {catalogo.isLoading && <p style={ayudaStyle}>Cargando colocadores...</p>}
      {catalogo.error && <p style={errorStyle}>No se pudo leer el catálogo de colocadores.</p>}
      {!catalogo.isLoading && !catalogo.error && opcionesColocador.length === 0 && (
        <p style={errorStyle}>
          Todavía no hay colocadores en el catálogo: middle office o un admin los cargan desde
          Licitaciones → Colocadores.
        </p>
      )}
      <div role="group" aria-label="Colocadores" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {opcionesColocador.map(colocador => {
          const elegido = seleccion.some(c => c.id === colocador.id);
          return (
            <label key={colocador.id} style={chipStyle(elegido)}>
              <input type="checkbox" checked={elegido} onChange={() => handleToggleColocador(colocador)} />
              {colocador.nombre}
              {colocador.inactivo && <span style={{ fontSize: '0.72rem', color: '#465163' }}>(inactivo)</span>}
            </label>
          );
        })}
      </div>
      {intentoGuardar && errorColocadores && <div style={errorStyle} role="alert">{errorColocadores}</div>}

      {seleccion.length > 0 && (
        <div className="axon-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem 1.5rem', marginTop: '0.85rem' }}>
          {seleccion.map(colocador => {
            const inputId = `fee-${colocador.id}`;
            const error = erroresFee[colocador.id];
            const conError = mostrarError(error, feeInputs[colocador.id]);
            return (
              <div key={colocador.id}>
                <label style={fieldStyles.label} htmlFor={inputId}>Fee {colocador.nombre} (bps)</label>
                <input
                  id={inputId} type="text" inputMode="decimal" value={feeInputs[colocador.id] ?? ''}
                  onChange={(e) => handleFeeColocadorChange(colocador.id, e.target.value)}
                  style={inputConError(conError)} placeholder="Ej: 25 (0 si no paga)"
                  aria-invalid={conError} aria-describedby={`${inputId}-ayuda`}
                />
                <div id={`${inputId}-ayuda`} style={conError ? errorStyle : ayudaStyle}>
                  {conError ? error : describeFeeAyuda(parseFeeBps(feeInputs[colocador.id]))}
                </div>
              </div>
            );
          })}
          {axonSeleccionado && (
            <div>
              <label style={fieldStyles.label} htmlFor="cuit-emisora">CUIT de la emisora</label>
              <input
                id="cuit-emisora" type="text" inputMode="numeric" name="cuitEmisora" value={form.cuitEmisora}
                onChange={handleInputChange}
                onBlur={(e) => setForm(prev => ({ ...prev, cuitEmisora: formatCuitForDisplay(e.target.value) }))}
                style={inputConError(cuitConError)}
                placeholder="Ej: 30-12345678-9"
                aria-invalid={cuitConError} aria-describedby="cuit-emisora-ayuda"
              />
              <div id="cuit-emisora-ayuda" style={cuitConError ? errorStyle : ayudaStyle}>
                {cuitConError ? errorCuitEmisora : 'Axon Capital es colocador: su fee lo paga la emisora.'}
              </div>
            </div>
          )}
        </div>
      )}

      {!usaColocadores && renderFeeLegacy()}
    </>);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={editingLicitacion ? 'Editar Licitación' : 'Nueva Licitación'}
      maxWidth="860px"
      onSubmit={handleSubmit}
      error={formError}
      footer={<>
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
        <button type="submit" style={fieldStyles.button} disabled={isSaving}>
          {isSaving ? 'Guardando...' : 'Guardar Licitación'}
        </button>
      </>}
    >
      <div className="axon-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.85rem 1.5rem' }}>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={fieldStyles.label}>Nombre</label>
          <input type="text" name="nombre" value={form.nombre} onChange={handleInputChange} style={fieldStyles.input} placeholder="Ej: Letras Chubut Serie CXVII" required />
        </div>
        <div>
          <label style={fieldStyles.label}>Emisor</label>
          <input type="text" name="emisor" value={form.emisor} onChange={handleInputChange} style={fieldStyles.input} placeholder="Ej: Provincia del Chubut" />
        </div>
        <div>
          <label style={fieldStyles.label}>Mercado (opcional)</label>
          <input type="text" name="mercado" value={form.mercado} onChange={handleInputChange} style={fieldStyles.input} placeholder="Ej: A3, BYMA, MAV" />
        </div>
        <div>
          <label style={fieldStyles.label}>Nro Agente (carga al sistema)</label>
          <input type="text" name="nroAgente" value={form.nroAgente} onChange={handleInputChange} style={fieldStyles.input} placeholder="Ej: AG.001" />
        </div>
        <div>
          <label style={fieldStyles.label} htmlFor="licitacion-fecha-licitacion">Fecha de Licitación</label>
          <input id="licitacion-fecha-licitacion" type="date" name="fechaLicitacion" value={form.fechaLicitacion} onChange={handleInputChange} style={fieldStyles.input} required />
        </div>
        <div>
          <label style={fieldStyles.label} htmlFor="licitacion-fecha-liquidacion">Fecha de Liquidación</label>
          <input id="licitacion-fecha-liquidacion" type="date" name="fechaLiquidacion" value={form.fechaLiquidacion} onChange={handleFechaLiquidacionChange} style={fieldStyles.input} required />
        </div>
        <div>
          <label style={fieldStyles.label}>Fecha de Caja (día hábil previo, editable)</label>
          <input type="date" name="fechaCaja" value={form.fechaCaja} onChange={handleInputChange} style={fieldStyles.input} required />
        </div>
        <div style={{ gridColumn: 'span 3' }}>
          <label style={fieldStyles.label}>Observaciones</label>
          <textarea name="observaciones" value={form.observaciones} onChange={handleInputChange} style={fieldStyles.textarea} />
        </div>
      </div>

      {renderColocadores()}

      <div style={{ ...fieldStyles.sectionHeader, marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Especies / Clases</span>
        <button type="button" onClick={handleAddEspecie} style={{ ...fieldStyles.button, padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}>
          <PlusIcon /> Agregar especie
        </button>
      </div>

      {especies.map((esp, index) => {
        const bloqueada = especieTieneOrdenes(esp.id);
        return (
          <div key={esp.id} className="axon-form-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '0.75rem 1rem',
            padding: '0.85rem', marginTop: '0.75rem', backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB', borderRadius: '6px', alignItems: 'end',
          }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={fieldStyles.label}>Denominación</label>
              <input type="text" value={esp.denominacion} onChange={(e) => handleEspecieChange(index, 'denominacion', e.target.value)} style={fieldStyles.input} placeholder="Ej: Letra Clase 1 41 días" required />
            </div>
            <div>
              <label style={fieldStyles.label}>Clase</label>
              <input type="text" value={esp.clase} onChange={(e) => handleEspecieChange(index, 'clase', e.target.value)} style={fieldStyles.input} placeholder="Ej: Clase 1" />
            </div>
            <div>
              <label style={fieldStyles.label}>Se licita</label>
              <select value={esp.modalidad} onChange={(e) => handleEspecieChange(index, 'modalidad', e.target.value)} style={fieldStyles.select} disabled={bloqueada}>
                {formOptions.modalidadLicitacion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div style={{ gridRow: 'span 2', alignSelf: 'center' }}>
              {especies.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveEspecie(index)}
                  style={{ background: 'none', border: 'none', cursor: bloqueada ? 'not-allowed' : 'pointer', color: bloqueada ? '#D1D5DB' : '#DC2626', padding: '0.25rem' }}
                  title={bloqueada ? 'La especie tiene órdenes cargadas' : 'Quitar especie'}
                  disabled={bloqueada}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
            <div>
              <label style={fieldStyles.label}>Moneda de emisión</label>
              <select value={esp.monedaEmision} onChange={(e) => handleEspecieChange(index, 'monedaEmision', e.target.value)} style={fieldStyles.select}>
                {formOptions.monedaEmisionLicitacion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label style={fieldStyles.label}>Nro Colocación</label>
              <input
                type="text"
                value={esp.nroColocacion ?? ''}
                onChange={(e) => handleEspecieChange(index, 'nroColocacion', e.target.value)}
                style={fieldStyles.input}
                placeholder="Ej: 2670"
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={fieldStyles.label}>Monedas de integración aceptadas</label>
              <div style={{ display: 'flex', gap: '1.25rem', paddingTop: '0.35rem' }}>
                {formOptions.monedaIntegracionLicitacion.map(moneda => (
                  <label key={moneda} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={esp.monedasIntegracion.includes(moneda)}
                      onChange={() => handleMonedaIntegracionToggle(index, moneda)}
                    />
                    {moneda}
                  </label>
                ))}
              </div>
            </div>
            {/* Especie en USD: el TC hace falta para el fee de colocación (se cobra en
                pesos) y, si el cliente integra pesos, para saber cuánto tiene que fondear. */}
            {esEspecieUsd(esp) && (
              <>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={fieldStyles.label}>
                    {requiereTipoCambio(esp) ? 'Tipo de cambio para integrar' : 'Tipo de cambio para el fee (pesos)'}
                  </label>
                  <input
                    type="text" inputMode="decimal" value={esp.tipoCambio ?? ''}
                    onChange={(e) => handleEspecieChange(index, 'tipoCambio', e.target.value)}
                    onBlur={(e) => handleEspecieChange(index, 'tipoCambio',
                      formatNumberForDisplay(parseNumberForState(e.target.value)))}
                    style={fieldStyles.input}
                    placeholder="Ej: 1.474,50"
                  />
                </div>
                <div style={{ gridColumn: 'span 2', alignSelf: 'end', fontSize: '0.75rem', color: '#6B7281', paddingBottom: '0.4rem', lineHeight: 1.4 }}>
                  Com. A 3500 del día hábil anterior a la licitación. Se puede dejar
                  vacío y completar cuando se conozca: hasta entonces los montos en
                  pesos y el fee de colocación de esta especie quedan pendientes.
                  {especiesConTipoCambio > 1 && (
                    <button
                      type="button"
                      onClick={() => handleAplicarTipoCambioATodas(esp.tipoCambio)}
                      style={{
                        display: 'block', marginTop: '0.3rem', background: 'none', border: 'none',
                        padding: 0, color: '#1A283E', fontSize: '0.75rem', fontWeight: 600,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}
                    >
                      Aplicar a las {especiesConTipoCambio} especies en USD
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </Modal>
  );
};

export default LicitacionFormModal;
